import React, { useEffect, useState } from "react";
import { FaArrowLeftLong } from "react-icons/fa6";
import styles from "../../components/TrancheBotV2/index.module.css";
import { getAllTranchesV2, getTrancheBotV2Analytics } from "../../service/api";
import ActivityCard from "../../components/ApprovedTranch/ActivityCard";
import { showNotification } from "../../service/showNotification";
import DetailedLogsTable from "../../components/TrancheBotV2/DetailedLogsTable";
import { formatPrice } from "../../utils/formatPrice";
import { useHistory } from "react-router-dom";
import { TrancheBotV2Status, TrancheModule } from "../../utils/constant";
import useDebounce from "../../hooks/useDebounce";

// simple skeleton loader
const SkeletonBox = ({ width, height }) => (
  <div
    style={{
      width,
      height,
      borderRadius: "6px",
      background:
        "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-loading 1.5s infinite",
    }}
  />
);

const TrancheAnalytics = () => {
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const debouncedSearchTerm = useDebounce(searchText, 500);

  const [analyticsData, setAnalyticsData] = useState(null);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const history = useHistory();

  const handleAnalyticsBackClick = () => {
    const url = `/tranche-bot-v2`;
    history.push(url);
  };

  const getAnalyticsDetails = async () => {
    try {
      setIsAnalyticsLoading(true);
      const res = await getTrancheBotV2Analytics();
      if (res.success) {
        setAnalyticsData(res.data);
      }
    } catch (error) {
      console.log(error);
      showNotification("error", "Failed to load analytics details.");
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  // pass filters into API
  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const response = await getAllTranchesV2({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        date: filterDate,
        showAll: true,
        moduleName: TrancheModule.TRANCHE_BOT,
      });

      if (response.success) {
        const mappedRows = response.data.docs.map((doc) => ({
          _id: doc._id,
          timestamp: new Date(doc.createdAt).toISOString().split("T")[0],
          trancheId: doc.trancheName || "N/A",
          opportunityId: doc.opportunityId,
          user: doc.createdBy?.name || "System",
          equity: "$" + doc.ecToDeploy || 0,
          itc: doc.itcPercent + "%" || 0,
          status: doc.status || "Pending",
          zohoTrancheId: doc.zohoTrancheId || "",
        }));

        setRows(mappedRows);
        setTotalDocs(response.data.totalDocs);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setRows([]);
      setTotalDocs(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterDate]);

  useEffect(() => {
    getAnalyticsDetails();
  }, []);

  return (
    <>
      <div className={styles.analyticsMainWrapper}>
        <h1 className={styles.analyticsMainTitle}>Tranche Bot</h1>
      </div>
      <div className={styles.mainContainer}>
        {/* Analytics Dashboard */}
        <div className={styles.analyticsContainer}>
          <div className={styles.analyticsHeaderContainer}>
            <h1 className={styles.proposedAssetsHeader}>Analytics Dashboard</h1>
            <div
              className={styles.analyticsHeader}
              onClick={handleAnalyticsBackClick}
            >
              <FaArrowLeftLong className={styles.backIcon} />
              <span>Back</span>
            </div>
          </div>

          <div className={styles.analyticsContainerBoxes}>
            <div className={styles.analyticsContainerBox}>
              <p>Total Tranches Created</p>
              <h1>
                {isAnalyticsLoading ? (
                  <SkeletonBox width="80px" height="24px" />
                ) : (
                  analyticsData?.totalTranches || 0
                )}
              </h1>
            </div>
            <div className={styles.analyticsContainerBox}>
              <p>Total Equity Matched</p>
              <h1>
                {isAnalyticsLoading ? (
                  <SkeletonBox width="100px" height="24px" />
                ) : (
                  "$" +
                  formatPrice(
                    analyticsData?.totalEquityMatched ??
                      rows.reduce((sum, row) => sum + row.equity, 0)
                  )
                )}
              </h1>
            </div>
            <div className={styles.analyticsContainerRedBox}>
              <p>Remaining Equity to Deploy</p>
              <h1>
                {isAnalyticsLoading ? (
                  <SkeletonBox width="100px" height="24px" />
                ) : (
                  "$" + formatPrice(analyticsData?.remainingEquityToDeploy ?? 0)
                )}
              </h1>
            </div>
          </div>

          {/* You can also replace these hard-coded progress values with API data */}
          <div className={styles.analyticsBars}>
            <h3 className={styles.analysisSubHeader}>
              Analysis of Unallocated Equity
            </h3>

            <div className={styles.analyticsProgressContainer}>
              <p>&lt; 2% Gap</p>
              <div className={styles.analyticsProgressBar}>
                <div
                  className={styles.analyticsProgressFill}
                  style={{
                    width: `${
                      analyticsData?.gapPercentages?.lessThan2?.toFixed(2) || 0
                    }%`,
                  }}
                ></div>
              </div>
              <p>{analyticsData?.gapBuckets?.lessThan2 || 0}</p>
            </div>

            <div className={styles.analyticsProgressContainer}>
              <p>2–5% Gap</p>
              <div className={styles.analyticsProgressBar}>
                <div
                  className={styles.analyticsProgressFillOrange}
                  style={{
                    width: `${
                      analyticsData?.gapPercentages?.between2And5?.toFixed(2) ||
                      0
                    }%`,
                  }}
                ></div>
              </div>
              <p>{analyticsData?.gapBuckets?.between2And5 || 0}</p>
            </div>

            <div className={styles.analyticsProgressContainer}>
              <p>&gt; 5% Gap</p>
              <div className={styles.analyticsProgressBar}>
                <div
                  className={styles.analyticsProgressFillRed}
                  style={{
                    width: `${
                      analyticsData?.gapPercentages?.greaterThan5?.toFixed(2) ||
                      0
                    }%`,
                  }}
                ></div>
              </div>
              <p>{analyticsData?.gapBuckets?.greaterThan5 || 0}</p>
            </div>
          </div>

          {/* Detailed Logs with pagination */}
          <DetailedLogsTable
            rows={rows}
            isLoading={isLoading}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalDocs={totalDocs}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            searchText={searchText}
            setSearchText={setSearchText}
            filterDate={filterDate}
            setFilterDate={setFilterDate}
          />
        </div>

        {/* Right Column: Activity */}
        <ActivityCard />
      </div>
    </>
  );
};

export default TrancheAnalytics;
